import { VehicleFormData } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import {
  Card,
  Form,
  Image,
  Modal,
  Tooltip,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import ImgCrop from 'antd-img-crop';
import AntdFeatherIcon from '../../AntdFeatherIcon';
import { Trash, UploadCloud } from 'react-feather';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { validateImage } from '@/utils/functions/validateImage';
import { compressImage } from '@/utils/functions/compressImage';
import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';
import { useState } from 'react';
import { colorTraslate } from '@/core/domain/people/Colors/Colors.types';
import Group from '../../Group';

import './style.less';

function getBase64(img: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(img);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

interface Props {
  disabledFields?: boolean;
}

export default function VehicleImage({ disabledFields = false }: Props) {
  const form = Form.useFormInstance<VehicleFormData>();
  const formValues = Form.useWatch([], form);
  const searchBy = Form.useWatch('searchBy', form);

  const hasLicensePlate = !!formValues?.licensePlate;
  const hasChassi = !!formValues?.chassi && formValues.chassi.length === 17;

  const handlerDisableFields =
    searchBy === 'Placa' ? !hasLicensePlate : !hasChassi;

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>();

  const vehicleImage = formValues?.image;
  const vehicleImageUpload = formValues?.imageUpload;

  const handlePreview = async (file: UploadFile<UploadArchiveResult>) => {
    if (!file.url && !file.preview) {
      file.preview = (await getBase64(file.originFileObj as Blob)) as string; // eslint-disable-line
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const customRequest = async ({ file, onSuccess }: UploadRequestOption) => {
    if (typeof file === 'string') {
      return;
    }

    validateImage(file, true);
    const compressedFile = await compressImage(file as File);

    form.setFieldValue('imageUpload', compressedFile);

    if (onSuccess) {
      onSuccess('ok');
    }
  };

  const handleRemoveImage = () => {
    Modal.confirm({
      title: 'Remover imagem do veículo',
      content: 'Deseja realmente remover a imagem?',
      onOk: () => {
        form.setFieldValue('image', undefined);
        form.setFieldValue('imageUpload', undefined);
      },
      okType: 'danger',
      okText: 'Remover',
    });
  };

  const vehicleColor = formValues?.color?.name?.split('.')[1];

  return (
    <>
      {!vehicleImage && (
        <div className="image-upload-container">
          <Form.Item noStyle name="imageUpload">
            <ImgCrop modalTitle="Editar imagem" quality={0.1}>
              <Upload
                listType="picture-card"
                accept="image/jpeg, image/png"
                maxCount={1}
                customRequest={customRequest}
                onPreview={handlePreview}
                onRemove={() => form.setFieldValue('imageUpload', undefined)}
                disabled={disabledFields || handlerDisableFields}
              >
                {!vehicleImageUpload && (
                  <div className="image-upload-container-label">
                    <AntdFeatherIcon icon={<UploadCloud />} />
                    <div>Adicionar imagem do veículo</div>
                  </div>
                )}
              </Upload>
            </ImgCrop>
          </Form.Item>

          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewVisible,
                onVisibleChange: (visible) => setPreviewVisible(visible),
              }}
              src={previewImage}
            />
          )}
        </div>
      )}

      {!!vehicleImage && (
        <Card className="image-card-container" style={{ position: 'relative' }}>
          <Tooltip
            title={
              vehicleImage.includes('fraga')
                ? 'A imagem é meramente ilustrativa do modelo, não representando a atual cor do veículo.'
                : undefined
            }
          >
            <div style={{ height: 348, display: 'flex', alignItems: 'center' }}>
              <Image
                src={vehicleImage}
                preview={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            {!!formValues.colorId && vehicleColor !== 'undefined' && (
              <div className="tag-color-container">
                <Group gap={10}>
                  <button style={{ backgroundColor: vehicleColor }} />

                  {vehicleColor && (
                    <Typography.Text>
                      {colorTraslate[vehicleColor]}
                    </Typography.Text>
                  )}
                </Group>
              </div>
            )}

            <div className="overlay">
              <div className="overlay-icon">
                <Trash size={20} onClick={handleRemoveImage} />
              </div>
            </div>
          </Tooltip>
        </Card>
      )}
    </>
  );
}
